import {
  Autocomplete,
  type AutocompleteProps,
  type AutocompleteRenderGroupParams,
  Chip,
  TextField,
  type TextFieldProps,
} from "@mui/material";
import {
  type CategoryOption,
  getCategoryOptionLabel,
} from "portal/utils/categoryCollection/category";
import { classes } from "portal/theme/theme";
import { getAutocompleteText } from "portal/utils/forms";
import { isNil } from "common/utils/identity";
import { useInfiniteScrollOnScroll } from "portal/utils/hooks/useInfiniteScroll";
import { useTranslation } from "react-i18next";
import CarbonLogo from "portal/images/logo_icon.svg?react";
import CopyIcon from "@mui/icons-material/ContentCopy";
import React, { FC, useCallback, useState } from "react";

export type PlantProfileCategoryAutocompleteValue =
  | CategoryOption
  | CategoryOption[]
  | Array<string | CategoryOption>
  | string
  | null;

type PlantProfileCategoryAutocompleteProps = Pick<
  AutocompleteProps<CategoryOption, boolean, false, boolean>,
  "loading" | "disabled" | "multiple" | "freeSolo" | "size" | "onBlur"
> &
  Pick<TextFieldProps, "label" | "name" | "error" | "helperText"> & {
    categoryOptions: CategoryOption[];
    value: PlantProfileCategoryAutocompleteValue;
    onChange: (value: PlantProfileCategoryAutocompleteValue) => void;
    onSearchInputChange: (text: string) => void;
    onLoadMore?: () => void;
    textFieldClassName?: string;
  };

const getSingleSelectInputLabel = (
  selected: PlantProfileCategoryAutocompleteValue
): string => {
  if (isNil(selected) || Array.isArray(selected)) {
    return "";
  }
  if (typeof selected === "string") {
    return selected;
  }
  return selected.displayName;
};

/**
 * Presentational plant-profile category picker (grouped by collection).
 * Data loading and Formik binding belong in wrappers.
 */
export const PlantProfileCategoryAutocomplete: FC<
  PlantProfileCategoryAutocompleteProps
> = ({
  label,
  categoryOptions,
  value,
  onChange,
  onSearchInputChange,
  onLoadMore,
  onBlur,
  loading = false,
  disabled = false,
  multiple = false,
  freeSolo = false,
  size = "medium",
  name,
  error,
  helperText,
  textFieldClassName,
}) => {
  const { t } = useTranslation();
  const selectedValueId =
    !multiple && value && !Array.isArray(value) && typeof value !== "string"
      ? value.id
      : undefined;
  const [inputValue, setInputValue] = useState(() =>
    multiple ? "" : getSingleSelectInputLabel(value)
  );
  const [prevSelectedValueId, setPrevSelectedValueId] =
    useState(selectedValueId);

  if (!multiple && selectedValueId !== prevSelectedValueId) {
    setPrevSelectedValueId(selectedValueId);
    setInputValue(
      selectedValueId === undefined ? "" : getSingleSelectInputLabel(value)
    );
  }

  const loadMore = useCallback(() => {
    onLoadMore?.();
  }, [onLoadMore]);

  const handleListboxScroll =
    useInfiniteScrollOnScroll<HTMLUListElement>(loadMore);

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, nextInputValue: string, reason: string) => {
      if (!multiple) {
        // MUI resets the text field when options refetch while a value is set.
        if (reason === "reset") {
          return;
        }
        setInputValue(nextInputValue);
      }

      if (reason === "input" || reason === "clear") {
        onSearchInputChange(nextInputValue);
      }
    },
    [multiple, onSearchInputChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      if (!multiple) {
        setInputValue(getSingleSelectInputLabel(value));
      }
    },
    [multiple, onBlur, value]
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, next: PlantProfileCategoryAutocompleteValue) => {
      if (!multiple) {
        setInputValue(getSingleSelectInputLabel(next));
      }
      onChange(next);
    },
    [multiple, onChange]
  );

  return (
    <Autocomplete<CategoryOption, boolean, false, boolean>
      {...getAutocompleteText(t)}
      multiple={multiple}
      freeSolo={freeSolo}
      size={size}
      disabled={disabled}
      loading={loading}
      options={categoryOptions}
      filterOptions={(options: CategoryOption[]) => options}
      getOptionLabel={(option: CategoryOption | string) =>
        typeof option === "string" ? option : option.displayName
      }
      {...(!multiple
        ? {
            inputValue,
            onInputChange: handleInputChange,
          }
        : {
            onInputChange: (
              _: React.SyntheticEvent,
              nextInputValue: string,
              reason: string
            ) => {
              if (reason === "input" || reason === "clear") {
                onSearchInputChange(nextInputValue);
              }
            },
          })}
      groupBy={(option: CategoryOption) => option.collection?.id ?? ""}
      slotProps={{
        listbox: {
          onScroll: handleListboxScroll,
        },
      }}
      renderGroup={({
        key,
        group: groupCollectionId,
        children,
      }: AutocompleteRenderGroupParams) => {
        const sample = categoryOptions.find(
          (option) => option.collection?.id === groupCollectionId
        );

        return (
          <li key={key}>
            <CategoryOptionGroupHeader
              label={sample?.collection?.name ?? groupCollectionId}
              isCarbon={sample?.collection?.isCarbon}
            />
            <ul className="p-0 list-none text-base">{children}</ul>
          </li>
        );
      }}
      renderOption={(props, option: CategoryOption) => {
        const { key, ...optionProps } = props;
        return (
          <li
            {...optionProps}
            key={`${option.collection?.id}-${option.id}-${option.versionId}`}
          >
            {getCategoryOptionLabel(t, option)}
          </li>
        );
      }}
      {...(multiple
        ? {
            renderValue: (
              selected: PlantProfileCategoryAutocompleteValue,
              getItemProps: (args: { index: number }) => object
            ) => {
              const values = Array.isArray(selected)
                ? selected
                : isNil(selected)
                ? []
                : [selected];
              return values.map((option, index) => {
                if (typeof option === "string") {
                  return (
                    <Chip
                      {...getItemProps({ index })}
                      key={`${option}-${index}`}
                      label={option}
                      size="small"
                    />
                  );
                }

                return (
                  <Chip
                    {...getItemProps({ index })}
                    key={option.id}
                    label={option.displayName}
                    size="small"
                    icon={
                      option.collection ? (
                        <CopyIcon className="text-sm text-inherit" />
                      ) : undefined
                    }
                  />
                );
              });
            },
          }
        : {})}
      // MUI Autocomplete uses null for an empty single-select value.
      // eslint-disable-next-line unicorn/no-null
      value={value ?? (multiple ? [] : null)}
      onBlur={handleBlur}
      isOptionEqualToValue={(option, selected) =>
        typeof selected === "string" ? false : option.id === selected.id
      }
      onChange={handleChange}
      renderInput={(parameters: TextFieldProps) => (
        <TextField
          {...parameters}
          name={name}
          size={size}
          error={error}
          helperText={helperText}
          label={label}
          className={textFieldClassName}
        />
      )}
    />
  );
};

const CategoryOptionGroupHeader: FC<{
  label: string;
  isCarbon?: boolean;
}> = ({ label, isCarbon = false }) => (
  <div
    className={classes(
      "text-white text-sm p-1 pl-2 uppercase flex gap-2 items-center",
      {
        "bg-carbon-orange": isCarbon,
        "bg-gray-600": !isCarbon,
      }
    )}
  >
    <div>{label}</div>
    {isCarbon && <CarbonLogo className="h-3" />}
  </div>
);
